import { Controller } from "@hotwired/stimulus"

const AUTOSAVE_DELAY = 3000 // 3 seconds

export default class extends Controller {
  static targets = ["sidebar", "toolbar", "wordCount", "saveStatus", "fullscreenBtn", "exitFullscreenBtn"]
  static classes = ["fullscreen"]
  static values = { formId: String }

  #autosaveTimer = null
  #isFullscreen = false

  connect() {
    this._onFullscreenChange = this.#handleFullscreenChange.bind(this)
    document.addEventListener("fullscreenchange", this._onFullscreenChange)

    // Listen for Trix content changes for word count + autosave indicator
    this.element.addEventListener("trix-change", this.#handleContentChange.bind(this))

    this.#updateWordCount()
  }

  disconnect() {
    document.removeEventListener("fullscreenchange", this._onFullscreenChange)
    clearTimeout(this.#autosaveTimer)
  }

  // Toggle distraction-free fullscreen writing mode
  toggleFullscreen(e) {
    e.preventDefault()
    this.#isFullscreen = !this.#isFullscreen

    if (this.#isFullscreen) {
      this.#enterFocusMode()
    } else {
      this.#exitFocusMode()
    }
  }

  // Called when the form changes (Trix, inputs)
  markDirty() {
    this.#showSaveStatus("unsaved")
    clearTimeout(this.#autosaveTimer)
    // No autosave — just show an indicator
  }

  // ── Private ──────────────────────────────────────────────────────────

  #handleContentChange() {
    this.#updateWordCount()
    this.#showSaveStatus("unsaved")
  }

  #updateWordCount() {
    const trixEditor = this.element.querySelector("trix-editor")
    if (!trixEditor || !this.hasWordCountTarget) return

    const text = trixEditor.editor?.getDocument().toString() || ""
    const words = text.trim().split(/\s+/).filter(Boolean).length
    this.wordCountTarget.textContent = `${words} word${words !== 1 ? "s" : ""}`
  }

  #showSaveStatus(state) {
    if (!this.hasSaveStatusTarget) return
    if (state === "unsaved") {
      this.saveStatusTarget.textContent = "Unsaved changes"
      this.saveStatusTarget.className = "text-xs text-amber-500 dark:text-amber-400 font-medium transition-all"
    } else if (state === "saved") {
      this.saveStatusTarget.textContent = "All changes saved"
      this.saveStatusTarget.className = "text-xs text-emerald-500 dark:text-emerald-400 font-medium transition-all"
    }
  }

  #enterFocusMode() {
    // Hide the sidebar
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("hidden")
    }

    // Show exit button
    if (this.hasExitFullscreenBtnTarget) this.exitFullscreenBtnTarget.classList.remove("hidden")
    if (this.hasFullscreenBtnTarget) this.fullscreenBtnTarget.classList.add("hidden")

    // Make editor max width
    const editorCol = this.element.querySelector("[data-editor-col]")
    if (editorCol) {
      editorCol.classList.remove("lg:col-span-8")
      editorCol.classList.add("lg:col-span-12", "max-w-4xl", "mx-auto")
    }

    document.body.classList.add("article-focus-mode")
  }

  #exitFocusMode() {
    // Show the sidebar again
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("hidden")
    }

    // Swap buttons
    if (this.hasExitFullscreenBtnTarget) this.exitFullscreenBtnTarget.classList.add("hidden")
    if (this.hasFullscreenBtnTarget) this.fullscreenBtnTarget.classList.remove("hidden")

    const editorCol = this.element.querySelector("[data-editor-col]")
    if (editorCol) {
      editorCol.classList.remove("lg:col-span-12", "max-w-4xl", "mx-auto")
      editorCol.classList.add("lg:col-span-8")
    }

    document.body.classList.remove("article-focus-mode")
  }

  #handleFullscreenChange() {
    if (!document.fullscreenElement && this.#isFullscreen) {
      this.#isFullscreen = false
      this.#exitFocusMode()
    }
  }
}
